import React, { useEffect, useState } from 'react';
import Icon from 'components/AppIcon';
import clientPortalService from 'services/clientPortalService';

const DocumentAccess = ({ language, userId }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!userId) return;
      
      try {
        setLoading(true);
        const docsData = await clientPortalService?.getDocuments(userId);
        const formattedDocs = docsData?.map(doc => ({
          id: doc?.id,
          name: doc?.name,
          nameSw: doc?.name,
          type: doc?.type,
          size: doc?.size,
          category: doc?.category,
          uploadDate: new Date(doc?.uploadDate)?.toLocaleDateString('en-GB'),
          uploadedBy: doc?.uploadedByUser?.fullName || 'System',
          status: doc?.status,
          description: doc?.description,
          descriptionSw: doc?.description,
          canDownload: doc?.canDownload,
          canView: doc?.canView,
          fileUrl: doc?.fileUrl
        })) || [];
        setDocuments(formattedDocs);
      } catch (error) {
        console.error('Error fetching documents:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();

    // Setup real-time subscription
    if (userId) {
      const unsubscribe = clientPortalService?.subscribeToUpdates(userId, {
        onDocumentUpdate: () => {
          fetchDocuments();
        }
      });

      return () => {
        unsubscribe();
      };
    }
  }, [userId]);

  const categories = [
    { id: 'all', label: 'All Documents', labelSw: 'Hati Zote', icon: 'FileText' },
    { id: 'legal', label: 'Legal Documents', labelSw: 'Hati za Kisheria', icon: 'Scale' },
    { id: 'court', label: 'Court Documents', labelSw: 'Hati za Mahakama', icon: 'Building' },
    { id: 'contract', label: 'Contracts', labelSw: 'Mikataba', icon: 'FileSignature' },
    { id: 'evidence', label: 'Evidence', labelSw: 'Ushahidi', icon: 'Camera' }
  ];

  const translations = {
    en: {
      documents: "Documents",
      searchDocuments: "Search documents...",
      uploadDate: "Upload Date",
      uploadedBy: "Uploaded By",
      fileSize: "File Size",
      status: "Status",
      actions: "Actions",
      view: "View",
      download: "Download",
      share: "Share",
      approved: "Approved",
      verified: "Verified",
      draft: "Draft",
      signed: "Signed",
      pending_review: "Pending Review",
      noDocuments: "No documents found",
      filterBy: "Filter by category"
    },
    sw: {
      documents: "Hati",
      searchDocuments: "Tafuta hati...",
      uploadDate: "Tarehe ya Kupakia",
      uploadedBy: "Aliyepakia",
      fileSize: "Ukubwa wa Faili",
      status: "Hali",
      actions: "Vitendo",
      view: "Ona",
      download: "Pakua",
      share: "Shiriki",
      approved: "Imeidhinishwa",
      verified: "Imethibitishwa",
      draft: "Rasimu",
      signed: "Imesainiwa",
      pending_review: "Inasubiri Ukaguzi",
      noDocuments: "Hakuna hati zilizopatikana",
      filterBy: "Chuja kwa kategoria"
    }
  };

  const t = translations?.[language];

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': case'verified': case'signed':
        return 'bg-success-100 text-success-700 border-success-200';
      case 'draft': case'pending_review':
        return 'bg-warning-100 text-warning-700 border-warning-200';
      default:
        return 'bg-secondary-100 text-secondary-700 border-secondary-200';
    }
  };

  const getFileIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'pdf':
        return 'FileText';
      case 'docx': case'doc':
        return 'FileText';
      case 'zip':
        return 'Archive';
      case 'jpg': case'jpeg': case'png':
        return 'Image';
      default:
        return 'File';
    }
  };

  const filteredDocuments = documents?.filter(doc => {
    const matchesCategory = selectedCategory === 'all' || doc?.category === selectedCategory;
    const matchesSearch = doc?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         doc?.nameSw?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleViewDocument = (doc) => {
    // Mock view functionality
    console.log('Viewing document:', doc?.name);
  };

  const handleDownloadDocument = (doc) => {
    // Mock download functionality
    console.log('Downloading document:', doc?.name);
  };

  const handleShareDocument = (doc) => {
    // Mock share functionality
    console.log('Sharing document:', doc?.name);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold text-text-primary">{t?.documents}</h1>
      </div>
      {/* Search and Filter */}
      <div className="bg-surface rounded-lg shadow-card p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" />
              <input
                type="text"
                placeholder={t?.searchDocuments}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="lg:w-64">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e?.target?.value)}
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {categories?.map(category => (
                <option key={category?.id} value={category?.id}>
                  {language === 'en' ? category?.label : category?.labelSw}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {/* Category Tabs */}
      <div className="bg-surface rounded-lg shadow-card p-6">
        <div className="flex flex-wrap gap-2 mb-6">
          {categories?.map(category => (
            <button
              key={category?.id}
              onClick={() => setSelectedCategory(category?.id)}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-smooth
                ${selectedCategory === category?.id
                  ? 'bg-primary text-white' :'bg-secondary-100 text-text-secondary hover:bg-secondary-200'
                }
              `}
            >
              <Icon name={category?.icon} size={16} />
              <span>{language === 'en' ? category?.label : category?.labelSw}</span>
            </button>
          ))}
        </div>

        {/* Documents List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : filteredDocuments?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="FileX" size={48} className="mx-auto text-text-secondary mb-4" />
            <p className="text-text-secondary">{t?.noDocuments}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredDocuments?.map(doc => (
              <div key={doc?.id} className="border border-border rounded-lg p-4 hover:shadow-card transition-smooth">
                <div className="flex items-start gap-4">
                  {/* File Icon */}
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={getFileIcon(doc?.type)} size={24} className="text-primary" />
                  </div>

                  {/* Document Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div>
                        <h3 className="font-medium text-text-primary">
                          {language === 'en' ? doc?.name : doc?.nameSw}
                        </h3>
                        <p className="text-sm text-text-secondary mt-1">
                          {language === 'en' ? doc?.description : doc?.descriptionSw}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(doc?.status)}`}>
                        {t?.[doc?.status]}
                      </span>
                    </div>

                    {/* Document Meta */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-text-secondary mb-4">
                      <div>
                        <span className="font-medium">{t?.uploadDate}:</span>
                        <br />
                        {doc?.uploadDate}
                      </div>
                      <div>
                        <span className="font-medium">{t?.uploadedBy}:</span>
                        <br />
                        {doc?.uploadedBy}
                      </div>
                      <div>
                        <span className="font-medium">{t?.fileSize}:</span>
                        <br />
                        {doc?.size}
                      </div>
                      <div>
                        <span className="font-medium">Type:</span>
                        <br />
                        {doc?.type}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {doc?.canView && (
                        <button
                          onClick={() => handleViewDocument(doc)}
                          className="flex items-center gap-2 px-3 py-2 bg-primary-100 text-primary rounded-lg hover:bg-primary-200 transition-smooth"
                        >
                          <Icon name="Eye" size={16} />
                          <span className="text-sm font-medium">{t?.view}</span>
                        </button>
                      )}
                      {doc?.canDownload && (
                        <button
                          onClick={() => handleDownloadDocument(doc)}
                          className="flex items-center gap-2 px-3 py-2 bg-success-100 text-success-700 rounded-lg hover:bg-success-200 transition-smooth"
                        >
                          <Icon name="Download" size={16} />
                          <span className="text-sm font-medium">{t?.download}</span>
                        </button>
                      )}
                      <button
                        onClick={() => handleShareDocument(doc)}
                        className="flex items-center gap-2 px-3 py-2 bg-secondary-100 text-text-secondary rounded-lg hover:bg-secondary-200 transition-smooth"
                      >
                        <Icon name="Share2" size={16} />
                        <span className="text-sm font-medium">{t?.share}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentAccess;